// apps/web/src/pages/compras/conferencia-dialogs.tsx — Fase 13, Plano 22 (COMP-03, EST-12, D-10, D-15, D-18).
// Diálogos da conferência da NF de compra.
// - VincularMaterialDialog: escolhe a matéria-prima do item SÓ no estado local; o vínculo fornecedor + código é gravado pelo
//   servidor na confirmação do recebimento (COMP-03: nada de vínculo antes do clique em "Confirmar recebimento").
// - DefinirFatorDialog: grava unidade de compra e fator DO MATERIAL (parâmetros de estoque, estoque:configurar). Isso é cadastro
//   do material, não vínculo da NF; sem a permissão o diálogo só orienta.
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { MaterialLookupCompras, ParametrosEstoqueMaterial, SalvarParametrosEstoqueInput } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { FormMessage } from '../../components/ui/form-message';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { MaterialCombobox } from '../../components/vendas/material-combobox';
import type { MaterialEscolhido } from '../../lib/compras/conferencia';
import { apiMutate } from '../../lib/csrf-fetch';
import { lerFatorDigitado, mensagemErroParametros, textoAjudaFator, unidadesIguais, validarParametrosEstoque } from '../../lib/estoque/parametros';
import { lerErroApi } from '../../lib/vendas/erro-api';

const ENDPOINT_MATERIAIS = '/api/v1/compras/lookups/materiais';

/** Material do lookup de Compras no formato do estado da conferência. */
export function materialDoLookup(material: MaterialLookupCompras): MaterialEscolhido {
  return {
    id: material.id,
    codigo: material.codigo,
    nome: material.nome,
    unidadeEstoque: material.unidadeEstoque,
    unidadeCompra: material.unidadeCompra,
    fatorConversao: material.fatorConversao,
  };
}

// ---------------------------------------------------------------------------------------------------------------------
// Vincular matéria-prima
// ---------------------------------------------------------------------------------------------------------------------

export interface VincularMaterialDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly fornecedorId: string | null;
  readonly fornecedorNome: string;
  readonly codigoProduto: string;
  /** Só atualiza o estado local da conferência. */
  readonly aoEscolher: (material: MaterialEscolhido) => void;
}

export function VincularMaterialDialog({ aberto, onAbertoChange, fornecedorId, fornecedorNome, codigoProduto, aoEscolher }: VincularMaterialDialogProps) {
  const [material, setMaterial] = useState<MaterialLookupCompras | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setMaterial(null);
    setErro(null);
  }, [aberto]);

  function vincular(): void {
    if (!material) {
      setErro('Selecione a matéria-prima.');
      return;
    }
    aoEscolher(materialDoLookup(material));
    onAbertoChange(false);
  }

  return (
    <Dialog open={aberto} onOpenChange={onAbertoChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Vincular matéria-prima ao código {codigoProduto}</DialogTitle>
          <DialogDescription>
            O vínculo vale para {fornecedorNome} + código {codigoProduto} nas próximas notas. Ele só é gravado ao confirmar o recebimento.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="vincular-material">Matéria-prima</Label>
          <MaterialCombobox<MaterialLookupCompras>
            id="vincular-material"
            value={material}
            onChange={(escolhido) => {
              setMaterial(escolhido);
              setErro(null);
            }}
            endpoint={ENDPOINT_MATERIAIS}
            parametros={fornecedorId ? { fornecedorId } : undefined}
            invalido={erro !== null}
          />
          {erro && <FormMessage>{erro}</FormMessage>}
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onAbertoChange(false)}>
            Voltar
          </Button>
          <Button type="button" onClick={vincular}>
            Vincular matéria-prima
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Definir fator
// ---------------------------------------------------------------------------------------------------------------------

export interface DefinirFatorDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly material: MaterialEscolhido | null;
  /** Unidade comercial da NF: pré-preenche "Unidade de compra". */
  readonly unidadeComercial: string;
  /** estoque:configurar. Sem a permissão o diálogo só orienta. */
  readonly podeConfigurar: boolean;
  readonly aoSalvar: (material: MaterialEscolhido) => void;
}

export function DefinirFatorDialog({ aberto, onAbertoChange, material, unidadeComercial, podeConfigurar, aoSalvar }: DefinirFatorDialogProps) {
  const [parametros, setParametros] = useState<ParametrosEstoqueMaterial | null>(null);
  const [unidadeCompra, setUnidadeCompra] = useState('');
  const [fator, setFator] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const materialId = material?.id ?? null;

  // Lê a versão atual dos parâmetros (lock otimista) ao abrir.
  useEffect(() => {
    if (!aberto || !podeConfigurar || materialId === null) return;
    let vivo = true;
    setParametros(null);
    setErro(null);
    setUnidadeCompra(unidadeComercial);
    setFator('');
    setCarregando(true);
    fetch(`/api/v1/estoque/materiais/${encodeURIComponent(materialId)}/parametros`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<ParametrosEstoqueMaterial>;
      })
      .then((dados) => {
        if (!vivo) return;
        setParametros(dados);
        setUnidadeCompra(dados.unidadeCompra ?? unidadeComercial);
        setFator(dados.fatorConversao === null ? '' : String(dados.fatorConversao).replace('.', ','));
      })
      .catch(() => {
        if (vivo) setErro('Não foi possível carregar os parâmetros deste material. Tente novamente.');
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, [aberto, podeConfigurar, materialId, unidadeComercial]);

  async function salvar(): Promise<void> {
    if (!parametros || !material || salvando) return;
    const problemas = validarParametrosEstoque({
      unidadeEstoque: parametros.unidadeEstoque,
      unidadeCompra,
      fator,
      tipoTinta: parametros.tipoTinta,
      altoGiro: parametros.altoGiroSemDevolucao,
    });
    if (problemas.length > 0) {
      setErro(problemas[0] ?? null);
      return;
    }
    const fatorLido = lerFatorDigitado(fator);
    const mesmaUnidade = unidadesIguais(parametros.unidadeEstoque, unidadeCompra);
    const corpo: SalvarParametrosEstoqueInput = {
      versao: parametros.versao,
      unidadeCompra: unidadeCompra.trim() === '' ? null : unidadeCompra.trim(),
      fatorConversao: mesmaUnidade || fatorLido.tipo !== 'ok' ? null : fatorLido.valor,
      estoquePreferencialId: parametros.estoquePreferencialId,
      altoGiroSemDevolucao: parametros.altoGiroSemDevolucao,
    };
    setSalvando(true);
    setErro(null);
    try {
      const res = await apiMutate(`/api/v1/estoque/materiais/${encodeURIComponent(material.id)}/parametros`, { method: 'PUT', body: corpo });
      if (!res.ok) {
        const falha = await lerErroApi(res, 'Não foi possível salvar o fator. Tente novamente.');
        setErro(mensagemErroParametros(falha.codigo) ?? falha.mensagem);
        return;
      }
      const salvo = (await res.json()) as ParametrosEstoqueMaterial;
      aoSalvar({ ...material, unidadeEstoque: salvo.unidadeEstoque, unidadeCompra: salvo.unidadeCompra, fatorConversao: salvo.fatorConversao });
      onAbertoChange(false);
    } catch {
      setErro('Não foi possível salvar o fator. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(proximo) => !salvando && onAbertoChange(proximo)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Definir fator{material ? ` — ${material.codigo}` : ''}</DialogTitle>
          <DialogDescription>
            {podeConfigurar
              ? 'Informe a unidade de compra e quantas unidades de estoque cabem em uma unidade de compra.'
              : 'Peça ao cadastro para informar o fator deste material.'}
          </DialogDescription>
        </DialogHeader>
        {podeConfigurar && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fator-unidade-compra">Unidade de compra</Label>
              <Input id="fator-unidade-compra" value={unidadeCompra} maxLength={20} disabled={carregando || parametros === null} onChange={(e) => setUnidadeCompra(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fator-conversao">Fator de conversão</Label>
              <Input id="fator-conversao" inputMode="decimal" value={fator} disabled={carregando || parametros === null} onChange={(e) => setFator(e.target.value)} />
              <p className="text-sm text-muted-foreground">{textoAjudaFator(parametros?.unidadeEstoque ?? material?.unidadeEstoque ?? '', unidadeCompra)}</p>
            </div>
            {erro && (
              <p role="alert" className="text-sm text-rose-700">
                {erro}
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="secondary" disabled={salvando} onClick={() => onAbertoChange(false)}>
            Voltar
          </Button>
          {podeConfigurar && (
            <Button type="button" disabled={salvando || carregando || parametros === null} onClick={() => void salvar()}>
              {salvando && <Loader2 className="animate-spin" aria-hidden="true" />}
              Salvar fator
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
